import React from "react";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import CardMedia from "@mui/material/CardMedia";
import { zIndexMap } from "@/ChnlListingPageV2/utils/zIndexMap";
import DtvTheme from "@directv-digital/dds.ui.dtv-theme";
import { IChnlLineupThemeUI } from "@/ChnlListingPageV2/server/themeConfig";
import Button from '@directv-digital/fusion.atoms.button';

const bps = DtvTheme.breakpoints;

interface CategoryHeaderProps {
  index: number;
  id: string;
  ariaControls: string;
  isAddOnCtgyHead: boolean;
  isMyFreeDtvCtgyHead: boolean;
  label: string;
  expanded: boolean;
  handleClick: (index: number) => void;
  headerHeight?: number;
  addOnPromoLogoInfo: {
    included: string;
    addon: string;
    promo: string;
  };
  theming?: IChnlLineupThemeUI["packagesTab"]["sectionHeaders"];
  myFreeConfig?: any;
  myfreeBgColor?: any;
};

const CategoryExpandIcon = ({ accOpen, iconColor }) => (
  <CardMedia
    alt="Click to toggle Category section"
    component="img"
    src="https://www.directv.com/dtvassets/global/icons/2022/arrow-down_dk-bg.svg"
    style={{
      height: "16px",
      width: "16px",
      ...accOpen && { transform: "rotate(180deg)" }
    }}
    sx={{
      [bps.up(bps.values.sm)]: {
        height: "20px",
        width: "20px"
      },
      color: iconColor,
      filter: "invert(100%) sepia(0%) saturate(7500%) hue-rotate(351deg) brightness(106%) contrast(104%)",
      position: "relative",
      cursor: "pointer",
      margin: 0,
      transition: "transform 200ms cubic-bezier(0.42, 0, 0.58, 1) 150ms"
    }}
  />
);

const AddOnInfoItem = ({ id, src, alt, textContent, textColor }) => (
  <DtvBox id={id} sx={{
    display: "flex",
    alignItems: "center",
    padding: "3px 0px 3px 0px",
    marginBottom: "6px !important",
    fontSize: "14px",
    borderBottom: "0px !important"
  }}>
    <CardMedia alt={alt} component="img"
      src={src}
      sx={{
        height: "24px",
        width: "24px",
        verticalAlign: "middle",
        display: "inline-flex"
      }}
    />
    <DtvText content={textContent}
      variant="smalltext"
      sx={{
        paddingLeft: "15px",
        [bps.down(bps.values.sm)]: {
          paddingLeft: "12.5px"
        },
        color: textColor
      }}
  />
  </DtvBox>
);

const CategoryHeader = ({
  index,
  id,
  ariaControls,
  isAddOnCtgyHead,
  isMyFreeDtvCtgyHead,
  label,
  expanded,
  handleClick,
  headerHeight,
  addOnPromoLogoInfo,
  theming,
  myFreeConfig,
  myfreeBgColor
}: CategoryHeaderProps): any => {
  // console.debug(
  //   "[PackagesGrid] CategoryHeader rendering", {
  //   index,
  //   isAddOnCtgyHead,
  //   label,
  //   expanded,
  //   handleClick,
  //   stickyAccordionStyles,
  //   addOnPromoLogoInfo
  // });
  const myFreeDTVCta = myFreeConfig?.packageData?.gpCtaProps;
  return (
    <AccordionSummary
      aria-controls={ariaControls}
      id={id}
      style={{
        position: "sticky",
        top: headerHeight,
        left: 0
      }}
      sx={{
        minHeight: "fit-content !important",
        // minHeight : "48px !important",
        // maxHeight: "48px !important", // Override default MUI value
        height: "48px !important",
        backgroundColor: theming?.background,
        color: theming?.textColor,
        borderBottom: `1px solid ${theming?.border}`,
        cursor: "default",
        display: "flex",
        flexDirection: "column",
        zIndex: zIndexMap.AccordionSummary, // 100
        // "& .MuiAccordionSummary-root": {
        // },
        ...(isMyFreeDtvCtgyHead && {
          padding: "0px !important"
        }),
        "& .MuiAccordionSummary-content": {
          margin: "12px 0 !important",
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
          "&: focus": {
            backgroundColor: "#7A7A7A",
            "& p": {
              textDecoration: 'underline'
            }
          },
          ...(isMyFreeDtvCtgyHead && {
            margin: "12px 0px 0px 0px !important",
            flexDirection: "column"
          }),
          "& .Mui-expanded": {
            minHeight: "auto" // set expanded height to same as the summary height to avoid jumping when expanded
          },
          [bps.down(bps.values.sm)]: {
            margin: "9px 0 !important",
            ...(isMyFreeDtvCtgyHead && {
              margin: "9px 0px 0px 0px !important"
            })
          }
        }
      }}
      onClick={() => handleClick(index)}>
        <DtvBox sx={{
          width: "100% !important",
          display: "flex",
          flexDirection: "row",
          ...(isMyFreeDtvCtgyHead && {
            padding: "0px 8px !important"
          })
        }}>
          <DtvBox sx={{
            display: "flex",
flexWrap: "wrap",
width: "100%"
          }}>
            <Typography sx={{
              width: "100%",
              fontFamily: "roboto-regular",
              fontSize: "16px",
              fontWeight: 400
            }}>
              {label}
            </Typography>

            {/** Category View Add-On Info-Sec Subheader */}
            {isAddOnCtgyHead && expanded &&
              <DtvBox id="addonPromo-info-container" sx={{
                width: "100%",
                margin: {
                  xs: "6px 0 0 0",
                  sm: "12px 0 0 0",
                  md: "12px 0 0 0"
                },
                display: "flex !important",
                [bps.down(bps.values.md)]: {
                  flexDirection: "column !important"
                },
                [bps.up(bps.values.md)]: {
                  alignItems: "center",
                  gap: "20px"
                }
              }}>
                <AddOnInfoItem
                  alt={"Included channels"}
                  id="addonPromo-info--included"
                  src={"https://www.directv.com/dtvassets/global/icons/component-controls/legend_check.svg"}
                  textColor={theming?.textColor}
                  textContent={addOnPromoLogoInfo.included || "Channel is included in package"}
                />
                <AddOnInfoItem
                  alt={"Add on channels"}
                  id="addonPromo-info--add-on"
                  src={"https://www.directv.com/dtvassets/global/icons/component-controls/legend_dollar.svg"}
                  textColor={theming?.textColor}
                  textContent={addOnPromoLogoInfo.addon || "Add-on channels (Channel can be added for an additional cost)"}
                />
                <AddOnInfoItem
                  alt={"Promo Available"}
                  id="addonPromo-info--promo"
                  src={"https://www.directv.com/dtvassets/global/icons/component-controls/legend_promotion.svg"}
                  textColor={theming?.textColor}
                  textContent={addOnPromoLogoInfo.promo || "Promotions (Channel is included for an introductory time)"}
                />
              </DtvBox>
            }
          </DtvBox>
          <DtvBox sx={{
            paddingRight: {
 xs: "4px",
sm: "12px",
md: "12px"
},
            alignSelf: "flex-start"
          }}>
            <CategoryExpandIcon
              accOpen={expanded}
              iconColor={theming?.textColor}
            />
          </DtvBox>
        </DtvBox>
         {/** myfreeDtv logo and cta */}
         {isMyFreeDtvCtgyHead && expanded && <DtvBox id="myfreeDtv-info-container" sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: myfreeBgColor,
            marginTop: "12px",
            gap: "32px"
        }}>
            <CardMedia
              alt={"MyFreeDTV"}
              component={"img"}
              loading="lazy"
              src={myFreeConfig?.logo}
              sx={{
 height: "27px",
width: "97px",
                [bps.down(bps.values.sm)]: {
                  height: "25px",
width: "87px"
                }
               }}
              onError={e => {
                  if (e.currentTarget.parentElement) {
 e.currentTarget.parentElement.style.display = "none"
}
              }}
            />
            {myFreeDTVCta && <Button
              buttontype="secondary"
              href={myFreeDTVCta?.targeturl || "#"}
              size="small"
            >
              {myFreeDTVCta?.label || "Sign Up"}
            </Button>}
        </DtvBox>}
    </AccordionSummary>
  );
};

export const MemoizedCategoryHeader = React.memo(CategoryHeader);
